import { useEffect, useState } from "react";
import { Plus, Trash2, Edit3, MousePointerClick, Package } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ImageField } from "@/components/admin/MediaPicker";

const inputCls = "w-full bg-ink-800 border border-ink-600 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand transition-colors text-white";
const labelCls = "text-xs text-slate-400 font-mono-label";

const CTA_EMPTY = { name: "", headline: "", description: "", button_text: "Learn more", url: "", image: "", tracking_id: "", disclosure: "" };
const AFF_EMPTY = { name: "", url: "", affiliate_url: "", image: "", description: "", disclosure: "", tracking_name: "" };

export default function Resources() {
  const [tab, setTab] = useState("ctas");
  const [ctas, setCtas] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [ctaForm, setCtaForm] = useState(CTA_EMPTY);
  const [affForm, setAffForm] = useState(AFF_EMPTY);
  const [ctaEdit, setCtaEdit] = useState(null);
  const [affEdit, setAffEdit] = useState(null);

  const load = () => {
    api.get("/admin/ctas").then((r) => setCtas(r.data)).catch(() => {});
    api.get("/admin/affiliates").then((r) => setAffiliates(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveCta = async () => {
    const data = ctaEdit || ctaForm;
    if (!data.name.trim()) { toast.error("Name required"); return; }
    try {
      if (ctaEdit) await api.put(`/admin/ctas/${ctaEdit.id}`, ctaEdit);
      else await api.post("/admin/ctas", ctaForm);
      toast.success("Saved"); setCtaForm(CTA_EMPTY); setCtaEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delCta = async (id) => { if (!window.confirm("Delete this CTA?")) return; try { await api.delete(`/admin/ctas/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const saveAff = async () => {
    const data = affEdit || affForm;
    if (!data.name.trim()) { toast.error("Name required"); return; }
    try {
      if (affEdit) await api.put(`/admin/affiliates/${affEdit.id}`, affEdit);
      else await api.post("/admin/affiliates", affForm);
      toast.success("Saved"); setAffForm(AFF_EMPTY); setAffEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delAff = async (id) => { if (!window.confirm("Delete this resource?")) return; try { await api.delete(`/admin/affiliates/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const cf = ctaEdit || ctaForm; const setCf = (patch) => ctaEdit ? setCtaEdit({ ...ctaEdit, ...patch }) : setCtaForm({ ...ctaForm, ...patch });
  const af = affEdit || affForm; const setAf = (patch) => affEdit ? setAffEdit({ ...affEdit, ...patch }) : setAffForm({ ...affForm, ...patch });

  return (
    <div className="max-w-4xl">
      <div className="mb-6"><div className="label-mono mb-1">Monetization</div><h1 className="font-display font-extrabold text-white text-3xl">CTAs & Affiliates</h1></div>

      <div className="flex items-center gap-1 border-b border-ink-700 mb-6">
        <button onClick={() => setTab("ctas")} data-testid="tab-ctas" className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 ${tab === "ctas" ? "border-brand text-brand" : "border-transparent text-slate-400 hover:text-white"}`}><MousePointerClick size={15} /> CTAs</button>
        <button onClick={() => setTab("affiliates")} data-testid="tab-affiliates" className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 ${tab === "affiliates" ? "border-brand text-brand" : "border-transparent text-slate-400 hover:text-white"}`}><Package size={15} /> Affiliate Resources</button>
      </div>

      {tab === "ctas" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-ink-600 bg-ink-900 p-5 space-y-3 h-fit">
            <div className="label-mono">{ctaEdit ? "Edit CTA" : "New CTA"}</div>
            <div><label className={labelCls}>Name (internal)</label><input value={cf.name} onChange={(e) => setCf({ name: e.target.value })} data-testid="cta-name" className={inputCls} /></div>
            <div><label className={labelCls}>Headline</label><input value={cf.headline} onChange={(e) => setCf({ headline: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Description</label><textarea value={cf.description} onChange={(e) => setCf({ description: e.target.value })} rows={2} className={inputCls} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelCls}>Button text</label><input value={cf.button_text} onChange={(e) => setCf({ button_text: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Destination URL</label><input value={cf.url} onChange={(e) => setCf({ url: e.target.value })} className={inputCls} /></div>
            </div>
            <div><label className={labelCls}>Tracking ID</label><input value={cf.tracking_id} onChange={(e) => setCf({ tracking_id: e.target.value })} className={inputCls} placeholder="auto from name" /></div>
            <div><label className={labelCls}>Disclosure (optional)</label><input value={cf.disclosure} onChange={(e) => setCf({ disclosure: e.target.value })} className={inputCls} /></div>
            <div className="flex gap-2"><button onClick={saveCta} data-testid="cta-save" className="inline-flex items-center gap-1.5 bg-brand text-brand-dark font-bold text-sm px-4 py-2 rounded-full"><Plus size={14} /> {ctaEdit ? "Update" : "Add CTA"}</button>{ctaEdit && <button onClick={() => setCtaEdit(null)} className="text-sm text-slate-400 px-3">Cancel</button>}</div>
          </div>
          <div className="space-y-3">
            {ctas.map((c) => (
              <div key={c.id} className="rounded-xl border border-ink-600 bg-ink-900 p-4">
                <div className="flex items-start justify-between">
                  <div><div className="text-white font-semibold">{c.name}</div><div className="text-xs text-slate-500 mt-0.5">{c.headline}</div></div>
                  <div className="flex gap-1"><button onClick={() => setCtaEdit(c)} className="p-1.5 text-slate-400 hover:text-brand"><Edit3 size={14} /></button><button onClick={() => delCta(c.id)} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button></div>
                </div>
                <div className="text-xs text-brand mt-2 font-mono-label truncate">{c.url}</div>
              </div>
            ))}
            {ctas.length === 0 && <div className="text-slate-500 text-sm py-8 text-center border border-dashed border-ink-600 rounded-xl">No CTAs yet.</div>}
          </div>
        </div>
      )}

      {tab === "affiliates" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-ink-600 bg-ink-900 p-5 space-y-3 h-fit">
            <div className="label-mono">{affEdit ? "Edit Resource" : "New Resource"}</div>
            <div><label className={labelCls}>Tool name</label><input value={af.name} onChange={(e) => setAf({ name: e.target.value })} data-testid="aff-name" className={inputCls} /></div>
            <div><label className={labelCls}>Description</label><textarea value={af.description} onChange={(e) => setAf({ description: e.target.value })} rows={2} className={inputCls} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelCls}>Destination URL</label><input value={af.url} onChange={(e) => setAf({ url: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Affiliate URL</label><input value={af.affiliate_url} onChange={(e) => setAf({ affiliate_url: e.target.value })} className={inputCls} /></div>
            </div>
            <ImageField value={af.image} onChange={(image) => setAf({ image })} label="Logo / image" />
            <div><label className={labelCls}>Tracking name</label><input value={af.tracking_name} onChange={(e) => setAf({ tracking_name: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Disclosure</label><input value={af.disclosure} onChange={(e) => setAf({ disclosure: e.target.value })} className={inputCls} /></div>
            <div className="flex gap-2"><button onClick={saveAff} data-testid="aff-save" className="inline-flex items-center gap-1.5 bg-brand text-brand-dark font-bold text-sm px-4 py-2 rounded-full"><Plus size={14} /> {affEdit ? "Update" : "Add resource"}</button>{affEdit && <button onClick={() => setAffEdit(null)} className="text-sm text-slate-400 px-3">Cancel</button>}</div>
          </div>
          <div className="space-y-3">
            {affiliates.map((a) => (
              <div key={a.id} className="rounded-xl border border-ink-600 bg-ink-900 p-4 flex items-center gap-3">
                {a.image && <img src={a.image} alt={a.name} className="w-10 h-10 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0"><div className="text-white font-semibold">{a.name}</div><div className="text-xs text-slate-500 truncate">{a.description}</div></div>
                <button onClick={() => setAffEdit(a)} className="p-1.5 text-slate-400 hover:text-brand"><Edit3 size={14} /></button>
                <button onClick={() => delAff(a.id)} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            ))}
            {affiliates.length === 0 && <div className="text-slate-500 text-sm py-8 text-center border border-dashed border-ink-600 rounded-xl">No resources yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
